import { DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, message } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import { CategoryEnum } from '../../../__generated__/graphql';
import { AppContext } from '../../../AppContext';
import { LOCAL_STORAGE } from '../../../common/constants';
import { isTabletBrowser } from '../../../common/utils';
import PdfShareComponents from '../../../components/PdfShareComponent';
import { AppActionType, AppContextType } from '../../../types/appContext.type';
import { HeaderData } from '../../../types/vastuIssueReport.type';
import { DOWNLOAD_FLOOR_PLAN } from '../graphql/queries';
import '../styles/downloadSharePdf.less';

export default function DownloadSharePdf() {
  const {
    state: { floorPlan },
    dispatch,
  } = useContext(AppContext) as AppContextType;
  const { width } = useWindowSize();
  const [headerData, setHeaderData] = useState<HeaderData>();
  const [loadingState, setLoadingState] = useState<{
    download: boolean;
    share: boolean;
  }>({
    download: false,
    share: false,
  });

  const [isShareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  const [fetchReport, { error: reportError }] = useLazyQuery(
    DOWNLOAD_FLOOR_PLAN,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (floorPlan) {
      setHeaderData(floorPlan as HeaderData);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(isTabletBrowser(width) ?? false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [width]);

  const handleReportAction = async (actionType: string) => {
    try {
      setLoadingState((prev) => ({ ...prev, [actionType]: true }));

      const { data } = await fetchReport({
        variables: { floorPlanId: headerData?.id },
      });

      const localHeaderData =
        JSON.parse(localStorage?.getItem(LOCAL_STORAGE.FLOOR_PLAN) || '') ||
        headerData;

      const floorPlanAsset = data?.downloadFloorPlanReportWeb?.url
        ? {
            key: data?.downloadFloorPlanReportWeb?.url,
            category: CategoryEnum.FloorPlanPdf,
          }
        : localHeaderData?.assets?.find(
            (asset: { category: CategoryEnum }) =>
              asset?.category === CategoryEnum.FloorPlanPdf,
          ) || headerData?.assets?.[0];

      if (floorPlanAsset?.key) {
        const newAsset = {
          key: floorPlanAsset?.key,
          category: CategoryEnum.FloorPlanPdf,
        };

        if (actionType === 'download') {
          window.open(floorPlanAsset.key, '_blank');
        } else if (actionType === 'share') {
          setShareUrl(floorPlanAsset.key);
          setShareModalOpen(true);
        }

        const updatedHeaderData = {
          ...headerData,
          assets: [
            ...(headerData?.assets || []).map((asset) => ({
              ...asset,
              key: asset.key,
              category: asset.category as CategoryEnum,
            })),
            newAsset,
          ],
        };

        dispatch({
          type: AppActionType.setFloorPlan,
          data: updatedHeaderData,
        });
      } else {
        message.error(reportError?.message || `Failed to ${actionType} report`);
        if (actionType === 'share') setShareModalOpen(false);
      }
    } catch {
      return message.error(`Error while ${actionType}ing report`);
    } finally {
      setLoadingState((prev) => ({ ...prev, [actionType]: false }));
    }
  };

  return (
    <div>
      {isSmallScreen ? (
        <div className="full-width">
          <div className="small-screen-btn">
            <Button
              type="primary"
              icon={<ShareAltOutlined />}
              className="share-btn"
              loading={loadingState.share}
              onClick={() => handleReportAction('share')}
            >
              Share
            </Button>

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              className="download-btn"
              loading={loadingState.download}
              onClick={() => handleReportAction('download')}
            >
              Download
            </Button>

            <PdfShareComponents
              onClose={() => setShareModalOpen(false)}
              modalOpen={isShareModalOpen}
              pdfShareurl={shareUrl}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="desktop-screen-btn">
            <Button
              type="default"
              icon={<ShareAltOutlined />}
              className="share-btn-s"
              loading={loadingState.share}
              onClick={() => handleReportAction('share')}
            >
              Share
            </Button>

            <Button
              type="primary"
              icon={<DownloadOutlined />}
              className="download-btn-s"
              loading={loadingState.download}
              onClick={() => handleReportAction('download')}
            >
              Download
            </Button>
          </div>
          <PdfShareComponents
            onClose={() => setShareModalOpen(false)}
            modalOpen={isShareModalOpen}
            pdfShareurl={shareUrl}
          />
        </>
      )}
    </div>
  );
}
